import { CloudUpload } from 'lucide-react'
import { DropzoneOptions, useDropzone } from 'react-dropzone'
import { RxPlus } from 'react-icons/rx'
import { v4 as uuidv4 } from 'uuid'
import { cn } from '@/lib/utils'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import { InputWrapper } from '../base/input-wrapper'
import { BaseInputProps } from '../base/types'
import { FileChipsDisplay } from './file-chips-display'

// 1GB in bytes
const MAX_FILE_SIZE = 1024 * 1024 * 1024

export type UploadFileFiles = Record<string, File>

type UploadFileProps = BaseInputProps & {
  allowMultiple: boolean
  className?: string
  disabled?: boolean
  displayImagesInline?: boolean
  dropzoneOptions: DropzoneOptions
  errorOnFail?: boolean
  files: UploadFileFiles
  /* Function to retrieve the file URL for a given file */
  getFileUrl?: (file: File) => string
  /* If provided, handleFileDelete will be used to delete the file */
  /* Otherwise, setFiles will be used to remove the file */
  handleFileDelete?: (file: File) => void
  /* If provided, onDrop will be used to handle the file drop */
  /* Otherwise, setFiles will be used to add the file */
  onDrop?: (files: File[]) => Promise<void>
  setFiles: (files: UploadFileFiles | ((prevState: UploadFileFiles) => UploadFileFiles)) => void
  useDirectory?: boolean
}

/**
 * Renders the dropzone area for file upload
 *
 * If `useDirectory` is true, the dropzone will accept only directories
 * If `allowMultiple` is true, the dropzone will accept multiple files
 * If `errorOnFail` is true, the dropzone will show an error message if a file fails to upload
 */
export function UploadFile({
  allowMultiple,
  className,
  disabled,
  displayImagesInline,
  dropzoneOptions,
  errorOnFail,
  files,
  getFileUrl,
  handleFileDelete,
  onDrop,
  setFiles,
  useDirectory,
  wrapperClassName,
  errorText,
  helperText,
  label,
  required,
  showDirtyIndicator,
  tooltip,
}: UploadFileProps) {
  const snackbar = useDossSnackbar()
  const { t } = useTypedTranslation()

  const onRemoveFile = (id: string) => {
    if (handleFileDelete) {
      handleFileDelete(files[id])
    } else {
      setFiles((currentFiles: Record<string, File>) => {
        const updatedFiles = { ...currentFiles }
        delete updatedFiles[id]
        return updatedFiles
      })
    }
  }

  const hasFiles = Object.keys(files).length > 0

  /* Handle file upload logic for single/multiple */
  const handleDropAccepted = async (newFiles: File[]) => {
    if (onDrop) {
      await onDrop(newFiles)
    } else {
      if (allowMultiple) {
        setFiles({
          ...files,
          ...newFiles.reduce((acc, file) => ({ ...acc, [uuidv4()]: file }), {}),
        })
      } else {
        /* Only keep the last file */
        setFiles({ [uuidv4()]: newFiles[newFiles.length - 1] })
      }
    }
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    useFsAccessApi: false,
    async onDropAccepted(newFiles) {
      await handleDropAccepted(newFiles)
    },
    onDropRejected(fileRejections) {
      if (errorOnFail) {
        fileRejections.forEach((file) => {
          snackbar.showError(
            t('dropzone.error', {
              fileName: file.file.name,
              error: file.errors[0]?.message,
            }),
          )
        })
      }
    },
    onDrop(files, fileRejections) {
      if (fileRejections.length > 0) {
        const tooLarge = fileRejections.find(
          (rejection) => rejection.errors[0].code === 'file-too-large',
        )
        if (tooLarge) {
          snackbar.showError(
            t('dropzone.file_too_large', {
              fileName: tooLarge.file.name,
            }),
          )
        }
      }
    },
    maxSize: MAX_FILE_SIZE,
    multiple: allowMultiple,
    ...dropzoneOptions,
    disabled,
  })

  const dropzoneHoverClasses = 'hover:bg-blue-1 hover:border-blue-7'
  const dropzoneDragActiveClasses = 'bg-blue-1 border-blue-7'
  const dropzoneDisabledClasses = 'bg-gray-1 border-gray-5 text-gray-8 cursor-default'

  return (
    <div className="flex w-full flex-col gap-2">
      <InputWrapper
        wrapperClassName={wrapperClassName}
        errorText={errorText}
        helperText={helperText}
        label={label}
        required={required}
        showDirtyIndicator={showDirtyIndicator}
        tooltip={tooltip}
      >
        {hasFiles && (
          <FileChipsDisplay
            disabled={disabled}
            displayImagesInline={displayImagesInline}
            files={files}
            getFileUrl={getFileUrl}
            onRemoveFile={onRemoveFile}
          />
        )}
        {/* Hidden input always rendered so open() can programmatically trigger it in all browsers (including Safari) */}
        <input {...getInputProps()} webkitdirectory={useDirectory ? 'true' : undefined} />
        {/* Show dropzone if there are no files */}
        {!hasFiles && (
          <div
            {...getRootProps()}
            className={cn(
              'flex flex-col items-center justify-center text-center gap-2 w-full p-4 border border-dashed rounded cursor-pointer transition-colors text-xs-tall',
              'text-gray-10 border-gray-5 bg-gray-1',
              !disabled && dropzoneHoverClasses,
              isDragActive && dropzoneDragActiveClasses,
              disabled && dropzoneDisabledClasses,
              className,
            )}
          >
            <CloudUpload size={18} strokeWidth={1.5} />
            <div className={cn('font-normal text-xs-tall')}>
              {t(useDirectory ? 'dropzone.dnd_directory' : 'dropzone.dnd_files')}{' '}
              <span className="underline">
                {t(useDirectory ? 'dropzone.upload_a_directory' : 'dropzone.upload_a_file')}
              </span>
            </div>
          </div>
        )}
      </InputWrapper>
      {hasFiles && !useDirectory && (
        <button
          type="button"
          className={cn(
            'pl-1 flex items-center gap-1.5 font-normal text-xs-tall text-gray-10',
            !disabled && 'hover:text-blue-9',
          )}
          onClick={disabled ? undefined : open}
          tabIndex={0}
          disabled={disabled}
        >
          <RxPlus />
          {allowMultiple ? t('dropzone.add_file') : t('dropzone.replace_file')}
        </button>
      )}
    </div>
  )
}
